import { MultiLineString } from "ol/geom";
import { Stroke, Style } from "ol/style";
import { asArray, asString } from "ol/color";
import { sampleAlong } from "./polyline.mjs";

export const LANE_TYPES = ["lane_center_line", "lane_trajectory_line"];

const ARROW_FRACTIONS_LONG = [1 / 3, 2 / 3];
const ARROW_FRACTIONS_SHORT = [0.5];
// HD lane models chop roads into many short adjacent segments (often 15-30m), each
// carrying its own lane_direction — with two arrows per feature that piles up into
// dense, overlapping clusters at junctions. Below this length (map units, roughly
// meters — ignores Mercator latitude stretch since this is just a clutter heuristic,
// not a measurement) a segment gets a single centered arrow instead of two.
const SHORT_LINE_LENGTH = 30;
// Zooming out brings many more lane segments into view at once (each still drawing
// its own arrow), so density is also capped by resolution (map units/px — roughly
// zoom 19 at the equator for a standard EPSG:3857 view), not just per-segment
// length: past this, arrows stop rendering entirely — at that scale they're too
// small to read and are pure clutter.
const ARROW_HIDE_RESOLUTION = 0.3;
// Faded rather than solid so the arrows read as annotation, not data.
const ARROW_OPACITY = 0.6;
// The arrow is drawn as plain strokes (shaft + two head wings) rather than the
// icon-arrow.png asset: the icon's source pixels are yellow and OL's Icon `color`
// tint is a per-channel multiply, so it can only darken toward black — a white halo
// copy of the icon is impossible. Vector strokes take any color. All lengths are in
// screen pixels (scaled by resolution). The arrow sits directly ON the line,
// centered on the sample point — the white halo plus the above-the-roads zIndex
// keep it legible over the line stroke (an offset-to-the-side placement was tried
// first and read as clutter next to dense parallel lanes). A "both" lane draws two
// opposite arrows centered on the same point, which merge into one double-headed
// arrow.
const SHAFT_LENGTH_PX = 9;
const SHAFT_WIDTH = 1.5;
const HEAD_LENGTH_PX = 4.5;
// Half-angle between each wing and the shaft, radians (~30°).
const HEAD_SPREAD = 0.5;
// White halo behind the whole arrow so the (dark) arrow still reads against dark
// basemaps (satellite, night styles).
const HALO_COLOR = "rgba(255, 255, 255, 1)";
const HALO_EXTRA_WIDTH = 2;
// Above every stroke the curvature/hdmap styles draw (their zIndexes go up to 11
// for hovered reconstructed curves) so the arrows always paint on top of the roads.
const HALO_Z_INDEX = 20;
const ARROW_Z_INDEX = 21;

function withAlpha(color, alpha) {
  const [r, g, b] = asArray(color);
  return asString([r, g, b, alpha]);
}

function polylineLength(coords) {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += Math.hypot(
      coords[i][0] - coords[i - 1][0],
      coords[i][1] - coords[i - 1][1],
    );
  }
  return total;
}

// Driving direction = geometry directionality (digitized start->end heading) combined
// with lane_direction (forward/backward/both/none). Each spec is a headingOffset
// against the digitized heading: forward [0], backward [PI], both [0, PI] (rendered
// as a double-headed arrow). "none"/missing returns [].
export function laneDirectionArrowSpecs(laneDirection) {
  if (!laneDirection || laneDirection === "none") {
    return [];
  }
  if (laneDirection === "both") {
    return [{ headingOffset: 0 }, { headingOffset: Math.PI }];
  }
  return [{ headingOffset: laneDirection === "backward" ? Math.PI : 0 }];
}

// Builds the halo+arrow Style pair for ONE arrow centered on `point`, given the
// geometry's digitized heading `rotation` and a `{headingOffset}` spec from
// laneDirectionArrowSpecs(). "backward"'s headingOffset of Math.PI flips the
// travel direction.
export function buildArrowStyles(
  point,
  rotation,
  headingOffset,
  color,
  resolution,
) {
  const travelHeading = rotation + headingOffset;
  const res = resolution || 1;
  const half = (SHAFT_LENGTH_PX / 2) * res;
  const tip = [
    point[0] + Math.cos(travelHeading) * half,
    point[1] + Math.sin(travelHeading) * half,
  ];
  const back = travelHeading + Math.PI;
  const backPoint = (angle, lengthPx) => [
    tip[0] + Math.cos(angle) * lengthPx * res,
    tip[1] + Math.sin(angle) * lengthPx * res,
  ];
  const arrow = new MultiLineString([
    [backPoint(back, SHAFT_LENGTH_PX), tip],
    [
      backPoint(back + HEAD_SPREAD, HEAD_LENGTH_PX),
      tip,
      backPoint(back - HEAD_SPREAD, HEAD_LENGTH_PX),
    ],
  ]);
  return [
    // Halo below, colored arrow on top — the two strokes trace the same
    // geometry, so zIndex alone decides which one shows.
    new Style({
      geometry: arrow,
      stroke: new Stroke({
        color: HALO_COLOR,
        width: SHAFT_WIDTH + HALO_EXTRA_WIDTH,
        lineCap: "round",
        lineJoin: "round",
      }),
      zIndex: HALO_Z_INDEX,
    }),
    new Style({
      geometry: arrow,
      stroke: new Stroke({
        color: withAlpha(color, ARROW_OPACITY),
        width: SHAFT_WIDTH,
        lineCap: "round",
        lineJoin: "round",
      }),
      zIndex: ARROW_Z_INDEX,
    }),
  ];
}

export function getLaneDirectionArrowStyles(
  feature,
  laneDirection,
  color,
  resolution,
) {
  const arrowSpecs = laneDirectionArrowSpecs(laneDirection);
  if (!arrowSpecs.length) {
    return [];
  }
  if (resolution && resolution > ARROW_HIDE_RESOLUTION) {
    return [];
  }
  const geometry = feature.getGeometry();
  if (typeof geometry.getCoordinates !== "function") {
    return [];
  }
  const coords = geometry.getCoordinates();
  if (!Array.isArray(coords) || coords.length < 2) {
    return [];
  }

  const arrowFractions =
    polylineLength(coords) < SHORT_LINE_LENGTH
      ? ARROW_FRACTIONS_SHORT
      : ARROW_FRACTIONS_LONG;

  const styles = [];
  for (const fraction of arrowFractions) {
    const { point, rotation } = sampleAlong(coords, fraction);
    for (const { headingOffset } of arrowSpecs) {
      styles.push(
        ...buildArrowStyles(point, rotation, headingOffset, color, resolution),
      );
    }
  }
  return styles;
}
